import { consume } from '@lit/context';
import { gsap } from 'gsap';
import { LitElement, css, html } from 'lit';
import { customElement, query, state } from 'lit/decorators.js';

import { gameApiContext } from '../../context';
import type { GameAPIType } from '../../GameAPI';
import { tokens } from '../../theme/tokens.css';
import { RADIO_CHANNELS, uiState as uiStateProxy, type RadioChannel } from '../../UIState';
import { bindValtio } from '../../ValtioController';
import { FUNCTION_KEY } from '../function-keys';

/**
 * Radio Config Panel - Customize radio quick messages
 *
 * Modal panel for editing the 10 preset messages per radio channel.
 * Supports save, cancel, restore channel defaults, and restore all defaults.
 *
 * @element radio-config-panel
 */
@customElement('radio-config-panel')
export class RadioConfigPanel extends LitElement {
  static override styles = [
    tokens,
    css`
      :host {
        display: block;
        /* Declared here rather than inherited from the body: the panel owns
           its type, and a tap to close it shouldn't start a text selection,
           callout or a grey flash across the whole screen. */
        font-family: var(--font-family);
        -webkit-user-select: none;
        user-select: none;
        -webkit-touch-callout: none;
        -webkit-tap-highlight-color: transparent;
      }

      .backdrop {
        position: fixed;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: var(--color-bg-backdrop);
        z-index: 200;
        /* Drops the 300ms double-tap delay from tap-to-close. */
        touch-action: manipulation;
      }

      .panel {
        position: relative;
        box-sizing: border-box;
        width: 550px;
        max-width: calc(100vw - 32px);
        /* Of the backdrop, as the help panel's: room for all ten messages in
           a browser window on a 1366x768 laptop. */
        max-height: 88%;
        background-color: var(--color-bg-modal);
        box-shadow: var(--shadow-panel);
        border-radius: var(--radius-lg);
        border-bottom: 2px solid var(--color-border-strong);
        overflow-y: auto;
        padding: 24px;
      }

      h2 {
        margin: 0 0 16px;
        font-size: var(--font-size-16);
        font-weight: 600;
        color: var(--color-text-white);
      }

      .channel-name {
        color: var(--color-players-active);
      }

      .hint {
        color: var(--color-text-secondary);
        font-size: var(--font-size-xs);
        margin-bottom: 16px;
      }

      .message-row {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 8px;
      }

      .message-index {
        color: var(--color-text-secondary);
        font-size: var(--font-size-13);
        min-width: 20px;
        text-align: right;
        flex-shrink: 0;
      }

      .message-input {
        flex: 1;
        min-width: 0;
        background-color: var(--color-bg-input);
        border: 1px solid var(--color-border-light);
        border-radius: var(--radius-sm);
        padding: 6px 10px;
        color: var(--color-text-primary);
        font-size: var(--font-size-13);
        font-family: inherit;
        outline: none;
        transition: border-color 0.15s;
        -webkit-user-select: text;
        user-select: text;
      }

      .message-input:focus {
        border-color: var(--color-text-secondary);
      }

      .buttons {
        display: flex;
        gap: 8px;
        margin-top: 16px;
      }

      .btn {
        padding: 8px 16px;
        border: none;
        border-radius: var(--radius-sm);
        font-size: var(--font-size-13);
        font-family: inherit;
        cursor: pointer;
        transition:
          background-color 0.15s,
          color 0.15s;
      }

      .btn-save {
        background-color: var(--color-btn-play);
        color: var(--color-text-white);
      }

      .btn-cancel {
        background-color: var(--color-bg-selected);
        color: var(--color-text-primary);
      }

      @media (hover: hover) {
        .btn-save:hover {
          background-color: var(--color-btn-play-hover);
        }

        .btn-cancel:hover {
          background-color: var(--color-bg-item-hover);
        }
      }

      .restore-links {
        display: flex;
        gap: 16px;
        margin-top: 12px;
      }

      /* Buttons, so Tab reaches them, dressed as links */
      .restore-link {
        padding: 0;
        background: none;
        border: none;
        color: var(--color-text-secondary);
        font-family: inherit;
        font-size: var(--font-size-xs);
        cursor: pointer;
        text-decoration: underline;
        transition: color 0.15s;
      }

      @media (hover: hover) {
        .restore-link:hover {
          color: var(--color-text-error);
        }
      }

      /* Inline confirmation */
      .confirm-bar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-top: 12px;
        padding: 10px 14px;
        background-color: rgba(255, 60, 60, 0.12);
        border: 1px solid rgba(255, 60, 60, 0.25);
        border-radius: var(--radius-sm);
      }

      .confirm-text {
        color: var(--color-text-primary);
        font-size: var(--font-size-sm);
      }

      .confirm-actions {
        display: flex;
        gap: 8px;
        flex-shrink: 0;
      }

      .btn-confirm-yes {
        background-color: var(--color-text-error);
        color: var(--color-text-white);
      }

      .btn-confirm-no {
        background-color: var(--color-bg-selected);
        color: var(--color-text-primary);
      }

      @media (hover: hover) {
        .btn-confirm-yes:hover {
          background-color: #ff4444;
        }

        .btn-confirm-no:hover {
          background-color: var(--color-bg-item-hover);
        }
      }
    `,
  ];

  private uiState = bindValtio(this, uiStateProxy);

  @consume({ context: gameApiContext })
  private api!: GameAPIType;

  @query('.panel') private panel?: HTMLElement;

  @query('.backdrop') private backdrop?: HTMLElement;

  /** Local editable copy of messages */
  @state() private editMessages: string[] = [];

  /** Pending confirm action: 'channel' | 'all' | null */
  @state() private confirmAction: 'channel' | 'all' | null = null;

  private channel: RadioChannel = 0;
  private closing = false;

  override connectedCallback(): void {
    super.connectedCallback();
    this.channel = this.uiState.radioConfigChannel ?? 0;
    this.editMessages = this.api.getRadioMessages(this.channel);
    document.addEventListener('keydown', this.handleKeyDown, { capture: true });
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.handleKeyDown, { capture: true });
    if (this.panel) gsap.killTweensOf(this.panel);
    if (this.backdrop) gsap.killTweensOf(this.backdrop);
  }

  /** Escape closes the panel. Consumed in capture phase so the in-game Escape
   *  path (UI.ts controlKey → closeAllPanels) doesn't also fire and drop the
   *  panel before its fade-out. If a restore confirm is showing, Escape backs
   *  that out first instead of dismissing the whole panel. */
  private handleKeyDown = (e: KeyboardEvent) => {
    if (e.key !== 'Escape') return;
    e.preventDefault();
    e.stopPropagation();
    // A held Escape repeats: the press backs out of the confirm, and the
    // repeats mustn't go on to close the panel and lose the edits
    if (e.repeat) return;
    if (this.confirmAction !== null) {
      this.confirmAction = null;
      return;
    }
    this.close();
  };

  /** Key presses inside the panel stop here, so typing in a box can't fly the
   *  plane, and Tab moves between the boxes and buttons rather than opening
   *  the scoreboard. Function keys carry on to the game (see FUNCTION_KEY).
   *  Releases aren't stopped at all: each reaches the game, so a key held as
   *  you click into a box can't stay stuck down. */
  private handlePanelKeyDown = (e: KeyboardEvent) => {
    if (FUNCTION_KEY.test(e.code)) return;
    e.stopPropagation();
  };

  override firstUpdated(): void {
    // Guard, don't reset: a close (Escape) that lands before first render must
    // not be undone into a ghost entrance.
    if (this.closing || !this.panel || !this.backdrop) return;
    // Entrance animation. fromTo (not from) so an interrupted tween can't
    // leave the panel stuck at its start values.
    gsap.fromTo(this.backdrop, { opacity: 0 }, { opacity: 1, duration: 0.2 });
    gsap.fromTo(this.panel, { y: 30, opacity: 0 }, { y: 0, opacity: 1, duration: 0.25, ease: 'power2.out' });
  }

  override render() {
    const channelName = RADIO_CHANNELS[this.channel].name;

    return html`
      <div class="backdrop" @click=${this.handleBackdropClick}>
        <div class="panel" @click=${(e: Event) => e.stopPropagation()} @keydown=${this.handlePanelKeyDown}>
          <h2>Customize <span class="channel-name">${channelName}</span></h2>
          <div class="hint">Use the | character to split a message into multiple parts sent with delays.</div>

          ${this.editMessages.map(
            (msg, i) => html`
              <div class="message-row">
                <span class="message-index">${(i + 1) % 10}.</span>
                <input
                  class="message-input"
                  type="text"
                  .value=${msg}
                  @input=${(e: InputEvent) => this.handleInput(i, e)}
                />
              </div>
            `,
          )}

          <div class="buttons">
            <button class="btn btn-save" @click=${this.handleSave}>Save</button>
            <button class="btn btn-cancel" @click=${this.handleCancel}>Cancel</button>
          </div>

          ${
            this.confirmAction !== null
              ? html`
                  <div class="confirm-bar">
                    <span class="confirm-text"
                      >${
                        this.confirmAction === 'channel'
                          ? 'Restore this channel to defaults?'
                          : 'Restore all channels to defaults?'
                      }</span
                    >
                    <div class="confirm-actions">
                      <button class="btn btn-confirm-yes" @click=${this.handleConfirmYes}>Yes</button>
                      <button class="btn btn-confirm-no" @click=${this.handleConfirmNo}>No</button>
                    </div>
                  </div>
                `
              : html`
                  <div class="restore-links">
                    <button class="restore-link" @click=${this.handleRestoreChannel}>Restore this channel</button>
                    <button class="restore-link" @click=${this.handleRestoreAll}>Restore all channels</button>
                  </div>
                `
          }
        </div>
      </div>
    `;
  }

  private handleInput(index: number, e: InputEvent): void {
    const input = e.target as HTMLInputElement;
    const updated = [...this.editMessages];
    updated[index] = input.value;
    this.editMessages = updated;
  }

  private handleSave(): void {
    this.api.playClick();
    this.api.saveRadioConfig(this.channel, this.editMessages);
    this.close();
  }

  private handleCancel(): void {
    this.api.playClick();
    this.close();
  }

  private handleBackdropClick(): void {
    this.close();
  }

  /** Fades out and closes the panel; unsaved edits are dropped. */
  private close(): void {
    this.animateOut(() => this.api.closePanel());
  }

  private handleRestoreChannel(): void {
    this.api.playClick();
    this.confirmAction = 'channel';
  }

  private handleRestoreAll(): void {
    this.api.playClick();
    this.confirmAction = 'all';
  }

  private handleConfirmYes(): void {
    this.api.playClick();
    if (this.confirmAction === 'channel') {
      this.api.restoreRadioChannel(this.channel);
    } else {
      this.api.restoreAllRadioMessages();
    }
    this.editMessages = this.api.getRadioMessages(this.channel);
    this.confirmAction = null;
  }

  private handleConfirmNo(): void {
    this.api.playClick();
    this.confirmAction = null;
  }

  private animateOut(onComplete: () => void): void {
    if (this.closing) return;
    this.closing = true;
    if (!this.panel || !this.backdrop) {
      onComplete();
      return;
    }
    gsap.killTweensOf(this.backdrop);
    gsap.killTweensOf(this.panel);
    gsap.to(this.backdrop, { opacity: 0, duration: 0.15 });
    gsap.to(this.panel, {
      y: 20,
      opacity: 0,
      duration: 0.15,
      ease: 'power2.in',
      onComplete,
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'radio-config-panel': RadioConfigPanel;
  }
}
